import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import WorkerLayout from "../components/WorkerLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { QrCode, Check, X, LogIn, LogOut, MapPin, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Scanner } from "@yudiel/react-qr-scanner";
import ChecklistModal, { ThankYouModal } from "../components/ChecklistModal";

export default function ScannerPage() {
  const [scanning, setScanning] = useState(true);
  const [result, setResult] = useState(null);
  const [recordType, setRecordType] = useState("entry");
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleScan = async (detectedCodes) => {
    if (detectedCodes && detectedCodes.length > 0 && scanning) {
      const code = detectedCodes[0].rawValue;
      
      if (code.startsWith("BADU:")) {
        setScanning(false);
        setResult(code);
        
        // Get location name
        try {
          const locationId = code.replace("BADU:", "");
          const response = await axios.get(`${API}/locations`);
          const loc = response.data.find(l => l.id === locationId);
          if (loc) {
            setLocation(loc);
          }
        } catch (error) {
          console.error("Error fetching location:", error);
        }
      } else {
        toast.error("QR Code inválido");
      }
    }
  };

  const handleError = (error) => {
    console.error("Scanner error:", error);
  };

  const confirmRecord = async () => {
    if (!result) return;
    
    setLoading(true);
    setGpsError(null);
    
    // Try to get GPS location
    let latitude = null;
    let longitude = null;
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true
        });
      });
      latitude = position.coords.latitude;
      longitude = position.coords.longitude;
    } catch (error) {
      setGpsError("Não foi possível obter a localização GPS. Ative o GPS e tente novamente.");
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/time-records`, {
        location_qr: result,
        type: recordType,
        latitude,
        longitude
      });
      
      toast.success(`${recordType === "entry" ? "Entrada" : "Saída"} registrada com sucesso!`);
      
      // If it's an exit, show the checklist
      if (recordType === "exit") {
        setCurrentRecordId(response.data.id);
        setShowChecklist(true);
      } else {
        navigate("/worker");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erro ao registrar ponto";
      toast.error(errorMsg);
      setGpsError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistComplete = () => {
    setShowChecklist(false);
    setShowThankYou(true);
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
    navigate("/worker");
  };

  const resetScanner = () => {
    setScanning(true);
    setResult(null);
    setLocation(null);
    setGpsError(null);
  };

  return (
    <WorkerLayout hideNav>
      <div className="min-h-screen flex flex-col bg-white" data-testid="scanner-page">
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-gray-100">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/worker")}
            className="text-gray-500 hover:text-gray-700"
            data-testid="back-btn"
          >
            <X className="mr-2 h-4 w-4" />
            Cancelar
          </Button>
          <h1 className="text-lg font-semibold text-gray-800">
            Escanear QR Code
          </h1>
          <div className="w-20"></div>
        </div>

        {/* Scanner or Result */}
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          {scanning ? (
            <div className="w-full max-w-sm">
              <Card className="bg-white border-gray-200 overflow-hidden shadow-lg">
                <CardContent className="p-0">
                  <div className="relative aspect-square">
                    <Scanner
                      onScan={handleScan}
                      onError={handleError}
                      scanDelay={300}
                      constraints={{
                        facingMode: "environment"
                      }}
                      styles={{
                        container: {
                          width: "100%",
                          height: "100%"
                        },
                        video: {
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }
                      }}
                    />
                    {/* Custom overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-12 border-2 border-[#F5A623] rounded-xl">
                        <div className="qr-scanner-corner top-left"></div>
                        <div className="qr-scanner-corner top-right"></div>
                        <div className="qr-scanner-corner bottom-left"></div>
                        <div className="qr-scanner-corner bottom-right"></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <p className="text-center text-gray-500 mt-6">
                Posicione o QR Code dentro da área de leitura
              </p>
            </div>
          ) : (
            <div className="w-full max-w-sm space-y-6">
              {/* Location Info */}
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <Check className="w-8 h-8 text-[#2ECC40]" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    QR Code Identificado
                  </h2>
                  {location && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-amber-50 rounded-lg">
                      <div className="flex items-center justify-center gap-2 text-[#F5A623]">
                        <MapPin size={18} />
                        <span className="font-semibold">{location.name}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{location.address}</p>
                      <p className="text-xs text-gray-400 mt-2">Raio permitido: {location.radius}m</p>
                    </div>
                  )}
                  
                  {gpsError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-red-600 text-left">{gpsError}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Record Type Selection */}
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={recordType === "entry" ? "default" : "outline"}
                  className={`h-20 flex-col gap-2 ${
                    recordType === "entry" 
                      ? "bg-[#2ECC40] hover:bg-[#27B038] text-white" 
                      : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                  onClick={() => setRecordType("entry")}
                  data-testid="entry-type-btn"
                >
                  <LogIn size={24} />
                  <span className="font-semibold">Entrada</span>
                </Button>
                <Button
                  variant={recordType === "exit" ? "default" : "outline"}
                  className={`h-20 flex-col gap-2 ${
                    recordType === "exit" 
                      ? "bg-[#F5A623] hover:bg-[#D99015] text-white" 
                      : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                  onClick={() => setRecordType("exit")}
                  data-testid="exit-type-btn"
                >
                  <LogOut size={24} />
                  <span className="font-semibold">Saída</span>
                </Button>
              </div>

              {/* Info about checklist for exit */}
              {recordType === "exit" && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <span className="font-medium">Nota:</span> Após registrar a saída, você deverá responder um breve checklist.
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={confirmRecord}
                  disabled={loading}
                  className="w-full h-14 btn-primary text-white font-bold text-lg"
                  data-testid="confirm-record-btn"
                >
                  {loading ? (
                    <div className="spinner w-6 h-6 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <>
                      <Check className="mr-2 h-5 w-5" />
                      Confirmar {recordType === "entry" ? "Entrada" : "Saída"}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={resetScanner}
                  className="w-full border-gray-200 text-gray-600 hover:bg-gray-50"
                  data-testid="scan-again-btn"
                >
                  <QrCode className="mr-2 h-4 w-4" />
                  Escanear Novamente
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Checklist Modal */}
        <ChecklistModal
          isOpen={showChecklist}
          onClose={() => {}}
          timeRecordId={currentRecordId}
          onComplete={handleChecklistComplete}
        />

        {/* Thank You Modal */}
        <ThankYouModal
          isOpen={showThankYou}
          onClose={handleThankYouClose}
        />
      </div>
    </WorkerLayout>
  );
}
